import { useMemo, useState } from "react";
import { ChevronDown, Download, FileText, Music, Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ChatAttachment } from "~/entities/chat/types";
import { downloadFromUrl } from "~/shared/lib/download";
import { fileNameFromKey } from "~/shared/lib/uploads/file-name";
import { cn } from "~/shared/lib/utils";
import { MediaLightbox, type MediaLightboxItem } from "~/shared/ui/media-lightbox";
import { VideoThumbnail } from "~/shared/ui/media-thumbnail";

interface MessageAttachmentsProps {
    attachments: ChatAttachment[];
}

interface FileThumbProps {
    attachment: ChatAttachment;
    onOpen: (attachment: ChatAttachment) => void;
}

function FileThumb({ attachment, onOpen }: FileThumbProps) {
    const fileName = fileNameFromKey(attachment.s3Key);
    const url = attachment.s3Url;
    // Звук открывается наравне с картинкой и видео: у него нет кадра, но есть
    // плеер в лайтбоксе. Иначе прикреплённое аудио можно было только скачать.
    const isOpenable =
        attachment.kind === "image" || attachment.kind === "video" || attachment.kind === "audio";

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        void downloadFromUrl(url).catch((err) => {
            console.error("download failed", err);
        });
    };

    const Body = () => {
        if (attachment.kind === "image") {
            return (
                <img
                    src={url}
                    alt={fileName}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    draggable={false}
                />
            );
        }
        if (attachment.kind === "video") {
            return <VideoThumbnail src={url} alt={fileName} className="h-full w-full" />;
        }
        if (attachment.kind === "audio") {
            // Значок воспроизведения — тот же приём, что у постера видео: без него
            // плитка звука выглядит нерабочей иконкой, хотя она открывает плеер.
            return (
                <div className="relative flex h-full w-full items-center justify-center text-violet-300">
                    <Music className="h-6 w-6" />
                    <span className="absolute right-1 bottom-1 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white">
                        <Play className="h-2.5 w-2.5 translate-x-px" aria-hidden />
                    </span>
                </div>
            );
        }
        return (
            <div className="flex h-full w-full items-center justify-center text-zinc-500">
                <FileText className="h-6 w-6" />
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-1">
            <div
                className="group relative aspect-square overflow-hidden rounded-md border border-white/5 bg-zinc-900"
                title={fileName}
            >
                <Body />

                {isOpenable && (
                    <button
                        type="button"
                        onClick={() => onOpen(attachment)}
                        aria-label="Open"
                        className={cn(
                            "absolute inset-0 bg-transparent transition-colors hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400",
                            // У звука увеличивать нечего — он открывается плеером.
                            attachment.kind === "audio" ? "cursor-pointer" : "cursor-zoom-in",
                        )}
                    />
                )}

                <button
                    type="button"
                    onClick={handleDownload}
                    aria-label="Download"
                    title="Download"
                    className={cn(
                        "absolute top-1 right-1 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/60",
                        "text-zinc-200 opacity-0 backdrop-blur-md transition-all",
                        "group-hover:opacity-100 hover:bg-black/80 hover:text-white focus-visible:opacity-100",
                    )}
                >
                    <Download className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* Имя файла подписью, а не только в `title`: у канала входа подпись
                есть, а «прочие файлы» до сих пор различались лишь по наведению,
                которого на тач-экране нет. */}
            <span className="line-clamp-1 text-center text-[11px] break-all text-zinc-400">{fileName}</span>
        </div>
    );
}

export function MessageAttachments({ attachments }: MessageAttachmentsProps) {
    const { t } = useTranslation("chat");
    const [open, setOpen] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    // Pictures, videos and audio share one lightbox feed so the preview can page
    // across every input of the message. Audio has no frame — the lightbox gives
    // it a player and the file name instead, which is the only way to listen to
    // an attachment at all.
    const lightboxItems = useMemo<MediaLightboxItem[]>(
        () =>
            attachments
                .filter((a) => a.kind === "image" || a.kind === "video" || a.kind === "audio")
                .map((a) => ({
                    url: a.s3Url,
                    type:
                        a.kind === "video"
                            ? ("video" as const)
                            : a.kind === "audio"
                              ? ("audio" as const)
                              : ("image" as const),
                    caption: fileNameFromKey(a.s3Key),
                })),
        [attachments],
    );

    if (attachments.length === 0) return null;

    const handleOpen = (attachment: ChatAttachment) => {
        const idx = lightboxItems.findIndex((item) => item.url === attachment.s3Url);
        if (idx >= 0) setLightboxIndex(idx);
    };

    return (
        <div className="mb-2">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="mb-1 flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
            >
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open ? "" : "-rotate-90")} />
                {t("message.inputFiles", { count: attachments.length })}
            </button>
            {open && (
                <div className="grid grid-cols-4 gap-2 md:grid-cols-6">
                    {attachments.map((att) => (
                        <FileThumb key={att.id} attachment={att} onOpen={handleOpen} />
                    ))}
                </div>
            )}

            <MediaLightbox
                items={lightboxItems}
                initialIndex={lightboxIndex ?? 0}
                isOpen={lightboxIndex !== null}
                onClose={() => setLightboxIndex(null)}
                showShare
            />
        </div>
    );
}
